import { type FC } from 'react';

import type { CarePlanViewModel } from '@eluve/api-contract';
import { Button, Icon, Stack, Tag, Text } from '@eluve/components';

const qualityTier = (done: number, total: number): string => {
  if (done >= total) return 'Complete';
  if (done >= total - 1) return 'Good';
  if (done >= Math.ceil(total / 2)) return 'Fair';
  return 'Weak';
};

export const ChecklistSection: FC<{
  checklist: CarePlanViewModel['checklist'];
  plan: CarePlanViewModel['plan'];
  disabled?: boolean;
  onConfirmCarePlan: () => void;
}> = ({ checklist, plan, disabled, onConfirmCarePlan }) => {
  const items = checklist.items.filter((item) => item.key !== 'reassessBooked');
  const doneCount = items.filter((item) => item.done).length;
  const total = items.length;
  const completed = items.every((item) => item.done);
  const midpoint = Math.ceil(total / 2);
  const left = items.slice(0, midpoint);
  const right = items.slice(midpoint);

  const renderItem = (
    item: CarePlanViewModel['checklist']['items'][number],
  ) => (
    <Stack key={item.key} direction="horizontal" align="center" gap={2}>
      {item.done ? (
        <Icon name="Check" size="xs" className="text-text-positive" />
      ) : (
        <Icon name="X" size="xs" className="text-text-tertiary" />
      )}
      <Text size="sm" color={item.done ? 'primary' : 'secondary'}>
        {item.label}
      </Text>
    </Stack>
  );

  return (
    <section className="w-full rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm">
      <Stack direction="horizontal" align="center" justify="between" gap={3}>
        <Text weight="semibold">Care plan checklist</Text>
        <Tag
          size="s"
          color={doneCount >= total ? 'positive' : 'warning'}
          label={`${qualityTier(doneCount, total)} · ${doneCount} of ${total}`}
        />
      </Stack>
      <div className="mt-3">
        <div className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
          <Stack direction="vertical" gap={2}>
            {left.map(renderItem)}
          </Stack>
          <Stack direction="vertical" gap={2}>
            {right.map(renderItem)}
          </Stack>
        </div>
        {plan && (
          <Stack
            direction="horizontal"
            align="center"
            wrap
            gap={3}
            className="mt-4 border-t border-border-secondary pt-3"
          >
            {plan.posture === 'accepted' ? (
              <>
                <Icon
                  name="CheckCircle"
                  size="sm"
                  className="text-text-positive"
                />
                <Text size="sm" className="text-text-positive">
                  Plan active
                  {plan.startDate ? ` since ${plan.startDate}` : ''}
                </Text>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  disabled={disabled || !completed}
                  onClick={onConfirmCarePlan}
                >
                  <Icon name="Check" size="auto" />
                  Confirm Care Plan
                </Button>
                <Text size="sm" color="secondary">
                  Confirm to activate this plan and carry it over to future
                  visits.
                </Text>
              </>
            )}
          </Stack>
        )}
      </div>
    </section>
  );
};
